import React, { useState, useRef, useEffect, ReactNode } from 'react';

interface ResizableSplitPanelProps {
  leftPanel: ReactNode;
  rightPanel: ReactNode;
  defaultLeftWidth?: number; // Percentage (default 60)
  minLeftWidth?: number;     // Percentage (default 30)
  maxLeftWidth?: number;     // Percentage (default 80)
}

export const ResizableSplitPanel: React.FC<ResizableSplitPanelProps> = ({
  leftPanel,
  rightPanel,
  defaultLeftWidth = 60,
  minLeftWidth = 30,
  maxLeftWidth = 80,
}) => {
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: PointerEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;

      // Calculate percentage
      let newLeftWidth = (mouseX / containerWidth) * 100;

      // Clamp to min/max
      newLeftWidth = Math.max(minLeftWidth, Math.min(maxLeftWidth, newLeftWidth));

      setLeftWidth(newLeftWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('pointermove', handleMouseMove);
      document.addEventListener('pointerup', handleMouseUp);
      document.addEventListener('pointercancel', handleMouseUp);
      // Prevent text selection while dragging
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    }

    return () => {
      document.removeEventListener('pointermove', handleMouseMove);
      document.removeEventListener('pointerup', handleMouseUp);
      document.removeEventListener('pointercancel', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, minLeftWidth, maxLeftWidth]);

  const handleMouseDown = (e: React.PointerEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  return (
    <>
      {/* Main container - only contains left panel and divider */}
      <div ref={containerRef} className="flex h-full w-full overflow-hidden">
        {/* Left Panel */}
        <div
          className="overflow-auto"
          style={{ width: `${leftWidth}%` }}
        >
          {leftPanel}
        </div>

        {/* Divider */}
        <div
          className={`
            w-1 bg-border hover:bg-primary/50 cursor-col-resize touch-none
            transition-colors flex-shrink-0 z-50
            ${isDragging ? 'bg-primary' : ''}
          `}
          onPointerDown={handleMouseDown}
          title="Arrastrar para redimensionar"
          style={{
            position: 'fixed',
            left: `${leftWidth}%`,
            top: 0,
            height: '100dvh'
          }}
        />

        {/* Spacer for divider */}
        <div style={{ width: '4px' }} />
      </div>

      {/* Right Panel - Fixed position, full viewport height */}
      <div
        className="overflow-hidden fixed top-0 right-0 z-40"
        style={{
          width: `calc(${100 - leftWidth}% - 4px)`,
          height: '100dvh',
          left: `calc(${leftWidth}% + 4px)`
        }}
      >
        {rightPanel}
      </div>
    </>
  );
};
